import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ManageAccounts from '@mui/icons-material/ManageAccounts';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import type { CSSProperties, KeyboardEvent } from 'react';
import { ClassStatus } from '../../types/ClassTable.type';
import { ClassCalendarItem } from '../../types/ClassCalendar.type';
import {
  formatCalendarDate,
  formatCalendarTimeRange,
  getEventDateKey,
} from '../../utils/classCalendar';
import ColorChip from '../common/ColorChip';
import SofLevelChip from '../common/SofLevelChip';
import { levelChipColors, serviceTypeColors } from '../../utils/colorHandler';

export interface ClassCalendarItemActions {
  onManage?: (item: ClassCalendarItem) => void;
  onEdit?: (item: ClassCalendarItem) => void;
  onDelete?: (item: ClassCalendarItem) => void;
}

interface ClassCalendarEventCardProps {
  item: ClassCalendarItem;
  onClick: (item: ClassCalendarItem) => void;
  actions?: ClassCalendarItemActions;
  dense?: boolean;
  showStudents?: boolean;
  style?: CSSProperties;
}

const statusLabel = (status: ClassStatus): string => {
  if (status === ClassStatus.ACTIVE) return 'Active';
  if (status === ClassStatus.CLOSED) return 'Closed';
  return 'Cancelled';
};

const statusColor = (status: ClassStatus): string => {
  if (status === ClassStatus.ACTIVE) return '#2e7d32';
  if (status === ClassStatus.CLOSED) return '#c62828';
  return '#b26a00';
};

const getCalendarClassName = (name: string): string =>
  name
    .trim()
    .replace(
      /\s*[-\u2013]\s*(?:Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+\d{1,2}:\d{2}\s*[-\u2013]\s*\d{1,2}:\d{2}\s*$/i,
      '',
    )
    .trim();

const getLevelPaletteKey = (level?: string | null): string | undefined => {
  const normalizedLevel = level?.trim().toLowerCase();
  if (!normalizedLevel) return undefined;

  const levelKeys = [
    'Starfish',
    'Seahorse',
    'Seal',
    'Barracuda',
    'Swordfish',
    'Dolphin',
    'Shark',
  ];

  return levelKeys.find((levelKey) => {
    const normalizedKey = levelKey.toLowerCase();
    return (
      normalizedLevel === normalizedKey ||
      normalizedLevel.startsWith(`${normalizedKey} `) ||
      normalizedLevel.startsWith(`${normalizedKey} (`)
    );
  });
};

const ClassCalendarEventCard = ({
  item,
  onClick,
  actions,
  dense = false,
  showStudents = false,
  style,
}: ClassCalendarEventCardProps) => {
  const dateKey = getEventDateKey(item);
  const coachLabel = item.coachTeachers.length
    ? item.coachTeachers.join(', ')
    : '---';
  const ariaLabel = [
    item.name,
    dateKey ? formatCalendarDate(dateKey) : 'Date not set',
    formatCalendarTimeRange(item.startTime, item.endTime),
    `Class Type ${item.subtypeName || '---'}`,
    `Level ${item.level || '---'}`,
    `Coach ${coachLabel}`,
    `Students ${item.students.length}`,
  ].join('. ');
  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onClick(item);
  };
  const hasActions = Boolean(
    actions?.onManage || actions?.onEdit || actions?.onDelete,
  );
  const calendarChipStyle: CSSProperties = {
    height: 22,
    boxSizing: 'content-box',
    alignItems: 'center',
  };

  return (
    <Box
      component="div"
      role="button"
      tabIndex={0}
      onClick={() => onClick(item)}
      onKeyDown={handleCardKeyDown}
      aria-label={ariaLabel}
      style={style}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: dense ? 0.35 : 0.55,
        width: '100%',
        minWidth: 0,
        p: dense ? 0.75 : 1,
        border: 0,
        borderLeft: `4px solid ${statusColor(item.status)}`,
        borderRadius: 1,
        backgroundColor:
          item.status === ClassStatus.ACTIVE
            ? alpha(theme.palette.primary.main, 0.14)
            : theme.palette.action.hover,
        color: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        boxSizing: 'border-box',
        overflow: 'hidden',
        transition: 'background-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.14),
          boxShadow: 1,
        },
        '&:focus-visible': {
          outline: '3px solid rgba(25, 118, 210, 0.45)',
          outlineOffset: 1,
        },
      })}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          lineHeight: 1.2,
          color: 'text.primary',
          whiteSpace: 'normal',
        }}
      >
        {formatCalendarTimeRange(item.startTime, item.endTime)}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <ColorChip
          label={item.subtypeName}
          palette={serviceTypeColors}
          emptyLabel="Class Type ---"
          style={calendarChipStyle}
        />
        <SofLevelChip
          level={item.level}
          serviceType={item.subtypeName}
          badgeSize={22}
          style={calendarChipStyle}
        />
      </Box>

      <Typography
        variant={dense ? 'caption' : 'body2'}
        sx={{
          fontWeight: 700,
          lineHeight: 1.25,
          overflowWrap: 'anywhere',
          whiteSpace: 'normal',
        }}
      >
        {getCalendarClassName(item.name) || item.name}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          fontSize: dense ? '0.8rem' : '0.875rem',
          fontWeight: 600,
          lineHeight: 1.3,
          textAlign: 'center',
          overflowWrap: 'anywhere',
          whiteSpace: 'normal',
        }}
      >
        Coach: {coachLabel}
      </Typography>

      {showStudents && (
        <Box
          sx={{
            mt: 0.25,
            p: 0,
            borderTop: '1px solid rgba(38, 54, 74, 0.12)',
            borderBottom: '1px solid rgba(38, 54, 74, 0.12)',
            borderLeft: '1px solid rgba(38, 54, 74, 0.12)',
            borderRight: '1px solid rgba(38, 54, 74, 0.12)',
            borderRadius: 0.5,
            maxHeight: dense ? 176 : 240,
            overflowY: 'auto',
          }}
        >
          <Box
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 28px minmax(0, 0.8fr)',
              backgroundColor: theme.palette.background.paper,
              borderBottom: '1px solid rgba(38, 54, 74, 0.18)',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              boxShadow: '0 1px 2px rgba(38, 54, 74, 0.12)',
              '& > *': {
                backgroundColor: theme.palette.background.paper,
              },
            })}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                px: 0.35,
                borderRight: '1px solid rgba(38, 54, 74, 0.14)',
              }}
            >
              Name
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                px: 0.35,
                borderRight: '1px solid rgba(38, 54, 74, 0.14)',
              }}
            >
              Age
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, px: 0.35 }}>
              Level
            </Typography>
          </Box>
          {item.students.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', px: 0.35, py: 0.25 }}
            >
              No students
            </Typography>
          ) : (
            item.students.map((student, index) => (
              <Box
                key={student.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 28px minmax(0, 0.8fr)',
                  alignItems: 'start',
                  borderBottom:
                    index < item.students.length - 1
                      ? '1px solid rgba(38, 54, 74, 0.1)'
                      : 'none',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.35,
                    overflowWrap: 'anywhere',
                    lineHeight: 1.25,
                    borderRight: '1px solid rgba(38, 54, 74, 0.1)',
                  }}
                >
                  {student.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.35,
                    lineHeight: 1.25,
                    borderRight: '1px solid rgba(38, 54, 74, 0.1)',
                  }}
                >
                  {student.age}
                </Typography>
                <Box sx={{ px: 0.35, minWidth: 0 }}>
                  <ColorChip
                    label={student.level}
                    palette={levelChipColors}
                    paletteKey={getLevelPaletteKey(student.level)}
                    emptyLabel="-"
                  />
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}

      <Typography
        variant="caption"
        sx={{
          color: statusColor(item.status),
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        Status: {statusLabel(item.status)}
      </Typography>

      {hasActions && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 0.25,
            mt: 0.1,
            pt: 0.25,
            borderTop: '1px solid rgba(38, 54, 74, 0.12)',
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {actions?.onManage && (
            <Tooltip title="Manage class">
              <IconButton
                size="small"
                color="primary"
                aria-label={`Manage class ${item.name}`}
                onClick={() => actions.onManage?.(item)}
                sx={{ p: 0.35 }}
              >
                <ManageAccounts fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {actions?.onEdit && (
            <Tooltip title="Edit class">
              <IconButton
                size="small"
                color="primary"
                aria-label={`Edit class ${item.name}`}
                onClick={() => actions.onEdit?.(item)}
                sx={{ p: 0.35 }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {actions?.onDelete && (
            <Tooltip title="Delete class">
              <IconButton
                size="small"
                color="error"
                aria-label={`Delete class ${item.name}`}
                onClick={() => actions.onDelete?.(item)}
                sx={{ p: 0.35 }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ClassCalendarEventCard;
